import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon } from '@heroicons/react/24/outline'
import { nutritionAPI } from '../../services/api'
import { mealLabel } from '../../utils/eatToday'

/**
 * VH-248: one tap logs something eaten before. "Recent" copies a recent meal onto today through
 * VH-181's single-meal copy; "Favourites" uses the favourite meals' add-to-today. Both are the
 * Nutrition page's own endpoints.
 */
export function RecentMeals({ date, onLogged }) {
  const { t } = useTranslation()
  const [tab, setTab] = useState('recent')
  const [done, setDone] = useState({})
  const [message, setMessage] = useState(null)

  const recent = useQuery({
    queryKey: ['eat', 'recent', date],
    queryFn: () => nutritionAPI.getRecentMeals(date).then((r) => r.data?.meals ?? []),
  })
  const favourites = useQuery({
    queryKey: ['eat', 'favourites'],
    queryFn: () => nutritionAPI.getFavoriteMeals({ limit: 30, sort_by: 'last_used_at', sort_desc: true })
      .then((r) => r.data?.favorite_meals ?? []),
    enabled: tab === 'favourites',
  })

  const add = async (key, call, label) => {
    setMessage(null)
    try {
      await call()
      setDone((d) => ({ ...d, [key]: true }))
      setMessage(t('eat.added', { meal: label }))
      onLogged?.()
    } catch (err) {
      setMessage(err?.response?.status === 409 ? t('eat.alreadyAdded') : t('eat.saveFailed'))
    }
  }

  const rows = tab === 'recent'
    ? (recent.data ?? []).map((m) => ({
      key: `r${m.id}`, label: mealLabel(m), kcal: m.total_calories, type: m.meal_type,
      call: () => nutritionAPI.copyMeal(m.id, date),
    }))
    : (favourites.data ?? []).map((f) => ({
      key: `f${f.id}`, label: f.name, kcal: f.total_calories, type: f.default_meal_type,
      call: () => nutritionAPI.addFavoriteToToday(f.id, null, date),
    }))
  const loading = tab === 'recent' ? recent.isLoading : favourites.isLoading

  return (
    <section className="bg-surface-container rounded-lg border border-gray-200 p-4 space-y-3" data-testid="eat-recent">
      <div className="flex gap-2" role="tablist">
        {['recent', 'favourites'].map((key) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} data-testid={`eat-tab-${key}`}
            onClick={() => { setTab(key); setMessage(null) }}
            className={`rounded-full border px-3 py-1.5 text-sm ${tab === key
              ? 'border-teal-700 bg-teal-50 text-teal-900' : 'border-gray-300 text-gray-700'}`}>
            {t(`eat.${key}`)}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-gray-600">{t('eat.loading')}</p>}
      {!loading && rows.length === 0 && <p className="text-sm text-gray-600">{t(`eat.${tab}Empty`)}</p>}
      <ul className="divide-y divide-gray-100">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center justify-between gap-3 py-2">
            <span className="min-w-0 text-sm">
              <span className="block truncate text-gray-900">{row.label}</span>
              <span className="text-xs text-gray-500">
                {t(`nutrition.mealTypes.${row.type}`, row.type)} · {Math.round(Number(row.kcal) || 0)} kcal
              </span>
            </span>
            <button type="button" disabled={!!done[row.key]} data-testid={`eat-add-${row.key}`}
              aria-label={t('eat.addThis', { meal: row.label })}
              onClick={() => add(row.key, row.call, row.label)}
              className={`shrink-0 rounded-full p-2 ${done[row.key] ? 'bg-green-100 text-green-800' : 'btn-primary'}`}>
              {done[row.key] ? '✓' : <PlusIcon className="h-5 w-5" aria-hidden="true" />}
            </button>
          </li>
        ))}
      </ul>
      {message && <p className="text-sm text-gray-700" role="status" data-testid="eat-recent-message">{message}</p>}
    </section>
  )
}

export default RecentMeals
